// Copy-paste integration snippets shown on the client "Integration" tab.
export interface SnippetInput {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scopes: string;
  confidential: boolean;
}

export type SnippetLang = "nextjs" | "php" | "python" | "go" | "java";

export const SNIPPET_LABELS: Record<SnippetLang, string> = {
  nextjs: "Next.js",
  php: "PHP",
  python: "Python",
  go: "Go",
  java: "Java (Spring)",
};

export function snippet(lang: SnippetLang, s: SnippetInput): string {
  const secret = s.confidential ? "process.env.OIDC_CLIENT_SECRET" : "undefined";
  switch (lang) {
    case "nextjs":
      return `// npm i openid-client  — app/lib/oidc.ts
import * as client from "openid-client";

export const config = await client.discovery(
  new URL("${s.issuer}"),
  "${s.clientId}",
  ${secret},
);

// Sign-in route: redirect the browser
const verifier = client.randomPKCECodeVerifier();
const url = client.buildAuthorizationUrl(config, {
  redirect_uri: "${s.redirectUri}",
  scope: "${s.scopes}",
  code_challenge: await client.calculatePKCECodeChallenge(verifier),
  code_challenge_method: "S256",
});

// Callback route
const tokens = await client.authorizationCodeGrant(config, new URL(request.url), {
  pkceCodeVerifier: verifier,
});
const claims = tokens.claims(); // sub (UUIDv7), email, roles, department…`;
    case "php":
      return `<?php // composer require jumbojett/openid-connect-php
use Jumbojett\\OpenIDConnectClient;

$oidc = new OpenIDConnectClient('${s.issuer}', '${s.clientId}', getenv('OIDC_CLIENT_SECRET'));
$oidc->setRedirectURL('${s.redirectUri}');
$oidc->addScope(explode(' ', '${s.scopes}'));
$oidc->setCodeChallengeMethod('S256');
$oidc->authenticate();

$sub   = $oidc->getVerifiedClaims('sub');
$roles = $oidc->getVerifiedClaims('roles');`;
    case "python":
      return `# pip install Authlib flask
from authlib.integrations.flask_client import OAuth

oauth = OAuth(app)
oauth.register(
    "platform",
    server_metadata_url="${s.issuer}/.well-known/openid-configuration",
    client_id="${s.clientId}",
    client_secret=os.environ.get("OIDC_CLIENT_SECRET"),
    client_kwargs={"scope": "${s.scopes}", "code_challenge_method": "S256"},
)

@app.route("/login")
def login():
    return oauth.platform.authorize_redirect("${s.redirectUri}")

@app.route("/callback")
def callback():
    token = oauth.platform.authorize_access_token()
    user = token["userinfo"]  # sub, email, roles…`;
    case "go":
      return `// go get github.com/coreos/go-oidc/v3/oidc golang.org/x/oauth2
provider, _ := oidc.NewProvider(ctx, "${s.issuer}")
conf := oauth2.Config{
    ClientID:     "${s.clientId}",
    ClientSecret: os.Getenv("OIDC_CLIENT_SECRET"),
    RedirectURL:  "${s.redirectUri}",
    Endpoint:     provider.Endpoint(),
    Scopes:       strings.Fields("${s.scopes}"),
}
verifier := oauth2.GenerateVerifier()
url := conf.AuthCodeURL(state, oauth2.S256ChallengeOption(verifier))

// callback
tok, _ := conf.Exchange(ctx, code, oauth2.VerifierOption(verifier))
idToken, _ := provider.Verifier(&oidc.Config{ClientID: conf.ClientID}).Verify(ctx, tok.Extra("id_token").(string))`;
    case "java":
      return `# application.yml — Spring Security OAuth2 Client
spring:
  security:
    oauth2:
      client:
        registration:
          platform:
            client-id: ${s.clientId}
            client-secret: \${OIDC_CLIENT_SECRET}
            scope: ${s.scopes.split(" ").join(", ")}
            redirect-uri: ${s.redirectUri}
            authorization-grant-type: authorization_code
        provider:
          platform:
            issuer-uri: ${s.issuer}`;
  }
}
